/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { observable, action, computed } from "mobx";
import { subscribeToSelector, unsubscribeFromSelectors } from "dc-preview-utils/src/Utils/MobXUtils";
import { debounce } from "utils/lodashUtils";
import * as stores from "stores";
import addEventListener from "dc-preview-utils/src/Utils/DomUtils";
import { isIOS, isMac, isTouchDevice } from "dc-preview-utils/src/Utils/DeviceUtils";

import { VERB_PREVIEW_SELECT, VERB_PREVIEW_PAN, SWITCH_PREVIEW_STATE } from "constants/enums";
import {
  READALOUD_CACHE_WINDOW,
  SPEECHSYNTHESIS_BOUNDARY_TIMEOUT,
  AJS_CUSTOM_SCROLL_EVT,
  READALOUD_TEXT_EXTRACT_PRIORITY
} from "constants/constants";
import { overlayEvents } from "overlays/OverlayEvents";
import { getPageAccText, getPageParaAccText } from "selectors/pageSelectors";
import { getSwitchPreviewState } from "selectors/appSelectors";
import {
  getPagesViewXPos,
  getPagesViewYPos,
  getTotalHeightTillPageTop,
  isInitialPageLoaded
} from "selectors/pagedViewState";
import { getPageBeforeOrAtOffset, getCurrentPageIndex } from "selectors/pageNumber";
import {
  getPagesInView,
  getTotalPages,
  isFullScreenOrFitOnePageMode,
  getDetectedPDFLanguage
} from "selectors/documentSelectors";
import { pagesInView } from "selectors/pagedViewSelectors";
import { getActiveVerb } from "selectors/quickToolsSelectors";
import {
  getPositionFromRect,
  isRectInView,
  setRectInView,
  flattenNode,
  SpeechSynthesisWrapper
} from "utils/ReadAloudUtils";
import { callExitCallback, setActiveVerbCallback } from "utils/QuickToolsUtils";
import { trackReadAloudEntry, trackReadAloudPlayingDuration } from "analytics/events";
import { locale2 as locale } from "dc-core";

class ReadAloudStore {
  @observable isReadAloudEnabled;
  @observable isReadAloudReady;
  @observable isReadAloudActive;
  @observable isPaused;
  @observable isPausedByButton;
  @observable pageIndex;
  @observable nodeIndex;
  @observable wordPosition;
  @observable voiceList;
  @observable selectedVoiceIndex;
  @observable selectedRateIndex;
  @observable currentWordRect;
  shouldSetDefaultVoice;

  constructor() {
    this.init();
  }

  initStore(docApi, stores) {
    if (docApi) {
      this.docApi = docApi;
    }
    if (stores) {
      this.stores = stores;
    }
    // Object is not created for unrelated tests. SpeechSynthesis is mocked for read aloud tests.
    this.synthesizer = window.speechSynthesis && new SpeechSynthesisWrapper(this.stores);
    if (this.synthesizer) this.synthesizer.init();
  }

  @action
  init() {
    this.isReadAloudEnabled = false;
    this.isReadAloudReady = false;
    this.isReadAloudActive = false;
    this.isPaused = false;
    this.isPausedByButton = false;
    this.pageIndex = 0;
    this.nodeIndex = 0;
    this.wordPosition = { start: 0, end: 0 };
    this.voiceList = [];
    this.selectedVoiceIndex = -1;
    this.voiceRateList = [0.5, 0.75, 1, 1.25, 1.5, 2];
    this.selectedRateIndex = 2;
    this.wordIndex = 0;
    this.autoScroll = false;
    this.isScrolling = false;
    this.lastPlayTime = null;
    this.shouldResetReadAloud = false;
    subscribeToSelector(
      stores,
      isInitialPageLoaded,
      isLoaded => isLoaded && this.isReadAloudEnabled && this.setIsReadAloudReady(true)
    );
    subscribeToSelector(stores, getSwitchPreviewState, state => {
      if (state === SWITCH_PREVIEW_STATE.PRE_SWITCH && this.isReadAloudActive) this.stopHandler();
    });
    if (this.synthesizer) this.synthesizer.pause();
    this.durationPlayedMs = 0;
    this.shouldSetDefaultVoice = true;
  }

  setScrollHandlers() {
    this.removeScrollHandler = addEventListener(
      window,
      AJS_CUSTOM_SCROLL_EVT,
      debounce(
        () => {
          this.isScrolling = true;
          !this.autoScroll && this.synthesizer.pause();
        },
        300,
        {
          leading: true,
          trailing: false
        }
      )
    );
    this.handleScrollEnd = async () => {
      if (!this.autoScroll) await this.setCurrentNodeInView();
      this.prefetchAccText();
      this.autoScroll = false;
      this.isScrolling = false;
      if (!this.isPausedByButton && this.isPaused) this.play();
    };
    overlayEvents.on("pagesViewScrollEnd", this.handleScrollEnd);
  }

  unsetScrollHandlers() {
    this.removeScrollHandler();
    overlayEvents.unregister("pagesViewScrollEnd", this.handleScrollEnd);
  }

  async startExtraction() {
    const { start, end } = pagesInView(this.stores);
    let found = false;
    for (let pageIndex = start; pageIndex < getTotalPages(this.stores); pageIndex++) {
      // eslint-disable-next-line no-await-in-loop
      await this.stores.documentStore.loadPageAccText(pageIndex, READALOUD_TEXT_EXTRACT_PRIORITY);
      const length = getPageParaAccText(this.stores, pageIndex).length || getPageAccText(this.stores, pageIndex).length;
      if (length) {
        found = true;
      }
      if (pageIndex >= end && found) break;
    }
    this.setIsReadAloudReady(true);
    this.prefetchAccText();
  }

  prefetchAccText() {
    const { start, end } = pagesInView(this.stores),
      numPages = getTotalPages(this.stores);
    for (let pageIndex = start; pageIndex < Math.min(numPages, end + READALOUD_CACHE_WINDOW); pageIndex++)
      this.stores.documentStore.loadPageAccText(pageIndex);
    for (let pageIndex = start - 1; pageIndex >= Math.max(0, start - READALOUD_CACHE_WINDOW); pageIndex--)
      this.stores.documentStore.loadPageAccText(pageIndex);
  }

  @action.bound
  enableReadAloud(value) {
    this.isReadAloudEnabled = value;
  }

  @action.bound
  setIsReadAloudReady(value) {
    this.isReadAloudReady = value;
  }

  @action.bound
  setIsReadAloudActive(value) {
    this.isReadAloudActive = value;
  }

  @action.bound
  setIsPaused(value) {
    this.isPaused = value;
  }

  @action.bound
  setIsPausedByButton(value) {
    this.isPausedByButton = value;
  }

  @computed
  get pagesText() {
    const pagesText = [];
    const numPages = getTotalPages(this.stores);
    for (let pageIndex = 0; pageIndex < numPages; pageIndex++) {
      let pageText = [];
      if (getPageParaAccText(this.stores, pageIndex)) {
        const paraAccText = getPageParaAccText(this.stores, pageIndex);
        paraAccText &&
          paraAccText.forEach(node => {
            pageText.push(node);
          });
        pageText = pageText.filter(node => node.contentDesc.trim() !== "");
      } else {
        const pageAccText = getPageAccText(this.stores, pageIndex);
        pageAccText && pageAccText.forEach(item => pageText.push(...flattenNode(item.node)));
        pageText = pageText.filter(node => node.contentDesc.trim() !== "");
      }

      // Check if node is vertical
      pageText.forEach(node => this.setNodeAlignment(node));
      pagesText.push(pageText);
    }
    return pagesText;
  }

  @action.bound
  setPageIndex(value) {
    this.pageIndex = value;
  }

  @action.bound
  setNodeIndex(value) {
    this.nodeIndex = value;
  }

  getNode() {
    return this.pagesText && this.pagesText[this.pageIndex] && this.pagesText[this.pageIndex][this.nodeIndex];
  }

  @action.bound
  advanceWordIndex() {
    const node = this.getNode();
    this.currentWordRect = undefined;
    if (!node || this.wordPosition.end === 0) {
      this.wordIndex = 0;
      return;
    }
    const wordCount = node.wordCoordinates.length;
    while (
      this.wordIndex < wordCount - 1 &&
      node.wordCoordinates[this.wordIndex].wordStartIndex < this.wordPosition.start
    )
      this.wordIndex++;
    const rect = node.contentRect;
    this.setCurrentWordRect(wordCount ? node.wordCoordinates[this.wordIndex].wordRect : rect);
  }

  @action.bound
  setCurrentWordRect(value) {
    this.currentWordRect = value;
    setRectInView(this.stores, this.pageIndex, this.currentWordRect, () => (this.autoScroll = true));
  }

  @action.bound
  setWordPosition(start, end) {
    this.wordPosition = { start, end };
    this.advanceWordIndex();
  }

  @action.bound
  setVoiceList(value) {
    this.voiceList = value;
  }

  manageDefaultVoiceSelection() {
    if (this.selectedVoiceIndex !== -1) return;
    this.setDefaultVoice(getDetectedPDFLanguage(stores));
    subscribeToSelector(stores, getDetectedPDFLanguage, detectedLanguage => {
      if (this.shouldSetDefaultVoice && detectedLanguage && detectedLanguage !== "un")
        this.setDefaultVoice(detectedLanguage);
    });
  }

  getLocaleMatches() {
    const localeString = locale.getLocale();
    const localeRegion = localeString && localeString.split("-")[1];
    return this.voiceList.filter(voice =>
      voice.lang.toLowerCase().endsWith(localeRegion && localeRegion.toLowerCase())
    );
  }

  setDefaultVoice(detectedLanguage) {
    let localeMatches = this.getLocaleMatches();
    let langMatches = [];
    let bestMatch;

    if (detectedLanguage && detectedLanguage !== "en") {
      langMatches = this.voiceList.filter(voice => voice.lang.startsWith(detectedLanguage));
      bestMatch = langMatches.find(voice => localeMatches.includes(voice)) || langMatches[0];
    }

    bestMatch = bestMatch || this.voiceList.find(voice => voice.default);

    const defaultVoiceIndex = this.voiceList.findIndex(voice => voice === bestMatch);
    if (defaultVoiceIndex !== -1) this.setSelectedVoiceIndex(defaultVoiceIndex);
  }

  @action.bound
  setSelectedVoiceIndex(value) {
    if (value >= 0 && value < this.voiceList.length) {
      this.selectedVoiceIndex = value;
      this.synthesizer && this.synthesizer.setVoice(this.voiceList[value]);
    }
  }

  setUserSelectedVoiceIndex(value) {
    this.setSelectedVoiceIndex(value);
    this.shouldSetDefaultVoice = false;
  }

  @action.bound
  handleMissingBoundaryEvent() {
    setTimeout(() => {
      const rect = this.getNode().contentRect;
      return !this.synthesizer.isFiringBoundaryEvt && this.setCurrentWordRect(rect);
    }, SPEECHSYNTHESIS_BOUNDARY_TIMEOUT);
  }

  @action.bound
  setSelectedRateIndex(value) {
    if (value >= 0 && value < this.voiceRateList.length) {
      this.selectedRateIndex = value;
      this.synthesizer.setRate(this.voiceRateList[value]);
    }
  }

  handleIPad() {
    // Ipad required the first speechSynthesis.speak method to be called as a direct response to user action
    // and not programmatically. So we are reading a dummy node at zero volume.
    const dummyUtterance = new SpeechSynthesisUtterance("");
    dummyUtterance.volume = 0;
    window.speechSynthesis.speak(dummyUtterance);
  }

  @action.bound
  async startHandler() {
    if (getDetectedPDFLanguage(stores) === undefined) {
      await stores.documentStore.fetchDetectedPDFLanguage();
    }
    this.stores.accessibilityStore.setUIElementToFocus("PlayPause");
    if (isIOS() || (isMac() && isTouchDevice())) this.handleIPad();
    this.setScrollHandlers();
    subscribeToSelector(stores, getCurrentPageIndex, async pageIndex => {
      if (isFullScreenOrFitOnePageMode(stores) && !this.isPausedByButton && pageIndex !== this.pageIndex) {
        this.synthesizer.pause();
        await this.setCurrentNodeInView();
        this.prefetchAccText();
        this.play();
      }
    });
    window.addEventListener("beforeunload", () => {
      this.sendDurationPlayedAnalytics();
      this.synthesizer && this.synthesizer.pause();
    });
    this.resetQuickTools();

    this.synthesizer.resetPosition();
    this.setIsReadAloudActive(true);
    await this.setCurrentNodeInView();
    this.play();
    this.startExtraction();
    this.setLastPlayedTime();
    trackReadAloudEntry();
    return true;
  }

  @action.bound
  stopHandler() {
    this.stores.accessibilityStore.setUIElementToFocus("readAloudStart");
    this.addDurationPlayed();
    this.synthesizer.pause();
    this.unsetScrollHandlers();
    unsubscribeFromSelectors(this.quickToolListener);
    this.setIsReadAloudActive(false);
  }

  async resetQuickTools() {
    const activeVerb = getActiveVerb(this.stores);
    if (activeVerb) await callExitCallback(activeVerb);
    this.stores.quickToolsStore.resetQuickTools();
    setActiveVerbCallback(VERB_PREVIEW_SELECT);
    this.quickToolListener = subscribeToSelector(this.stores, getActiveVerb, activeVerbId => {
      if (activeVerbId !== VERB_PREVIEW_SELECT && activeVerbId !== VERB_PREVIEW_PAN) this.pauseButtonHandler();
    });
  }

  async playButtonHandler() {
    await this.resetQuickTools();
    if (this.shouldResetReadAloud) {
      this.stores.pagedViewState.goToLocation(0, getPagesViewXPos(stores));
      this.currentWordRect = undefined;
      this.shouldResetReadAloud = false;
    }
    await this.setCurrentNodeInView();
    this.setLastPlayedTime();
    this.play();
  }

  play() {
    if (this.pageIndex !== undefined) this.synthesizer.play();
    else {
      this.shouldResetReadAloud = true;
      this.pauseButtonHandler();
    }
  }

  pauseButtonHandler() {
    this.addDurationPlayed();
    this.synthesizer.pauseByButton();
  }

  next() {
    this.synthesizer.next();
  }

  prev() {
    this.synthesizer.prev();
  }

  async setCurrentNodeInView() {
    if (this.currentWordRect) {
      const { isInViewY, isInViewX } = isRectInView(this.stores, this.pageIndex, this.currentWordRect);
      if (isInViewX && isInViewY) return;
    }
    const position = await this.findNextNodeInView();
    if (position === undefined) return;
    const { pageIndex, nodeIndex } = position;
    this.synthesizer.resetPosition();
    this.setPageIndex(pageIndex);
    this.setNodeIndex(nodeIndex);
  }

  async findNextNodeInView() {
    if (isFullScreenOrFitOnePageMode(stores)) {
      const pageIndex = getCurrentPageIndex(stores);
      await this.stores.documentStore.loadPageAccText(pageIndex, READALOUD_TEXT_EXTRACT_PRIORITY);
      return { pageIndex, nodeIndex: 0 };
    }
    const { yInPage } = getPageBeforeOrAtOffset(this.stores),
      startPageIndex = Math.max(0, getPagesInView(this.stores).start + (yInPage === -1)),
      endPageIndex = getPagesInView(this.stores).end;

    // Find first visible node in pages in view
    for (let pageIndex = startPageIndex; pageIndex <= endPageIndex; pageIndex++) {
      // eslint-disable-next-line no-await-in-loop
      await this.stores.documentStore.loadPageAccText(pageIndex, READALOUD_TEXT_EXTRACT_PRIORITY);
      const pageText = this.pagesText[pageIndex],
        pageTopHeight = getTotalHeightTillPageTop(this.stores, pageIndex),
        scrolledHeight = getPagesViewYPos(this.stores);
      for (let nodeIndex = 0; nodeIndex < pageText.length; nodeIndex++) {
        const node = pageText[nodeIndex],
          rect = node.contentRect,
          { top } = getPositionFromRect(rect, pageIndex, this.stores),
          rectTop = Math.ceil(top + pageTopHeight);
        if (rectTop >= scrolledHeight) return { pageIndex, nodeIndex };
      }
    }
    // If no node visible, iterate through remaining pages and get the next node.
    for (let pageIndex = endPageIndex + 1; pageIndex < this.pagesText.length; pageIndex++) {
      // eslint-disable-next-line no-await-in-loop
      await this.stores.documentStore.loadPageAccText(pageIndex, READALOUD_TEXT_EXTRACT_PRIORITY);
      if (this.pagesText[pageIndex].length > 0) return { pageIndex, nodeIndex: 0 };
    }
    return { pageIndex: undefined, nodeIndex: undefined };
  }

  setNodeAlignment(node) {
    node.isVertical = false;
    for (let i = 0; i < node.wordCoordinates.length - 1; i++) {
      const { wordRect: currRect } = node.wordCoordinates[i],
        { wordRect: nextRect } = node.wordCoordinates[i + 1];
      if (currRect.xMax === nextRect.xMax) {
        // If consecutive word rects have same xMax, node is likely to be vertical
        // If they have the samed yMin, node is likely to be horizontal
        node.isVertical = true;
        return;
      } else if (currRect.yMin === nextRect.yMin) return;
    }
  }

  setLastPlayedTime() {
    this.lastPlayTime = new Date().getTime();
  }

  getDurationPlayed() {
    if (this.isPausedByButton || !this.lastPlayTime) return this.durationPlayedMs;
    const stopTime = new Date().getTime();
    return this.durationPlayedMs + (stopTime - this.lastPlayTime);
  }

  addDurationPlayed() {
    this.durationPlayedMs = this.getDurationPlayed();
    this.lastPlayTime = null;
  }

  sendDurationPlayedAnalytics() {
    const durationPlayedMs = this.getDurationPlayed();
    if (durationPlayedMs > 0) trackReadAloudPlayingDuration(durationPlayedMs / 1000);
  }
}

export { ReadAloudStore };
